import { useTranslation } from "react-i18next"
import { MouseEvent, ReactNode, useEffect, useState } from "react"
import { useStore } from "../../contexts/store"
import { HexColorPicker } from "react-colorful"
import { Button, Box, Menu } from "@mui/material"

export const ColorBlock = ({color, selectedColor, onClick, size=30}: {color: string, selectedColor:string, onClick:any, size?:number}) => (
    <Box onClick={() => onClick(color)} sx={{
      backgroundColor: color,
      boxShadow: (color !== selectedColor) ? 0 : 5,
      height: size+"px",
      minWidth: size+"px",
      "&:hover": {
        backgroundColor: color,
        boxShadow: 5
      }
    }} />
  )

export const ColorList = ({list, title, selectedColor, onClick, size=30}: {list: string[] | null, title: string, selectedColor:string, onClick:any, size?:number}) => {
  const padding = size/6
    if(list && list.length > 0) {
      return (
        <Box sx={{padding: "0 "+padding+"px", margin: padding+"px 0", color: "#000e53", fontSize: "14px"}}>
          {title}
          <Box sx={{display: "flex", flexWrap: "wrap", gridGap: padding+"px", marginTop: "3px"}}>
            {list.map((color:string) =>
              <ColorBlock key={color} color={color} selectedColor={selectedColor} onClick={onClick} size={size} />
            )}
          </Box>
        </Box>
      )
    }
    return null
  }

const ColorPicker = ({children=null, color, setColor, sx}: {children?: null|ReactNode, color: string, setColor: (selectedColor: string) => void, sx?: any}) => {
  const { t } = useTranslation()
  const { uiStore, podStore } = useStore()
  const [selectedColor, setSelectedColor] = useState<string>("")
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const annotationColors = uiStore.annotationColors
  const customColors = podStore.getCustomAnnotationColors()

  useEffect(() => {
    if(color) setSelectedColor(color)
  }, [color])

  const handlePickerOpen = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handlePickerClose = (isCanceled: boolean) => {
    // if not explicitly cancelled, transfer colour when closing the modal
    if(!isCanceled && selectedColor) setColor(selectedColor)
    setAnchorEl(null)
  }

  const surface = children ? <div onClick={(e:any) => handlePickerOpen(e)}>{children}</div> : <Button
        id="color-picker-button"
        onClick={handlePickerOpen}
        sx={{
          backgroundColor: color,
          height: "35px",
          opacity: "60%",
          minWidth: "35px",
          width: uiStore.deviceWidth === "tiny" ? "fitContent" : "100%",
          "&:hover": {
            backgroundColor: color,
            boxShadow: 5
          },
          ...sx
        }}
      />

  return (
    <Box>
      {surface}
      <Menu
        id="color-picker-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={() => handlePickerClose(false)}
        MenuListProps={{
          'aria-labelledby': 'color-picker-button',
        }}
        sx={{padding: "10px", ".MuiPaper-root":{ backgroundColor: "white"}}}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'center',
        }}
      >
        <Box sx={{ borderRadius: "5px", display: "grid", margin: "10px 25px 5px 25px", maxWidth: "200px"}}>
          <HexColorPicker  color={selectedColor} onChange={setSelectedColor} />
          <ColorList title={t('Preselected Colors')} list={annotationColors} selectedColor={selectedColor} onClick={setSelectedColor} />
          <ColorList title={t('Custom Colors')} list={customColors} selectedColor={selectedColor} onClick={setSelectedColor} />
          {/* button group */}
          <Box sx={{
            display: "grid",
            gridGap: "10px",
            gridTemplateColumns: "min-content min-content",
            justifyContent: "end",
            marginTop: "20px"
          }}>
            <Button onClick={() => {handlePickerClose(true)}} variant="outlined">{t('Cancel')}</Button>
            <Button onClick={() => handlePickerClose(false)} variant="contained">{t('Ok')}</Button>
          </Box>
        </Box>
      </Menu>
    </Box>
  )
}

export default ColorPicker